import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'bizy-greeting-impressions';
const INTRODUCTORY_THRESHOLD = 4;

export type GreetingVariant = 'introductory' | 'familiar' | 'canceled_admin' | 'canceled_non_admin';

function getImpressionCount(): number {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? parseInt(stored, 10) || 0 : 0;
	} catch {
		return 0;
	}
}

function incrementImpressionCount(): number {
	const next = getImpressionCount() + 1;
	try {
		localStorage.setItem(STORAGE_KEY, String(next));
	} catch {
		// localStorage unavailable
	}
	return next;
}

export function getGreetingVariant(isCanceled: boolean, isAdmin: boolean, impressionCount: number): GreetingVariant {
	if (isCanceled) {
		return isAdmin ? 'canceled_admin' : 'canceled_non_admin';
	}
	return impressionCount < INTRODUCTORY_THRESHOLD ? 'introductory' : 'familiar';
}

export function useBizyGreeting(isVisible: boolean, isCanceled: boolean, isAdmin: boolean) {
	const [impressionCount, setImpressionCount] = useState(getImpressionCount);
	const hasCountedRef = useRef(false);

	useEffect(() => {
		if (isVisible && !hasCountedRef.current && !isCanceled) {
			hasCountedRef.current = true;
			incrementImpressionCount();
		}
		if (!isVisible) {
			hasCountedRef.current = false;
			setImpressionCount(getImpressionCount());
		}
	}, [isVisible, isCanceled]);

	const variant = getGreetingVariant(isCanceled, isAdmin, impressionCount);

	return { variant, impressionCount };
}

// ─── New-segment greeter callout (BIZY-638) ──────────────────────────
//
// A distinct greeting surface from the empty-state `useBizygreeting` variant
// above: when a *new* chat segment is about to start (the server's 8h-boundary
// `isNewSegment` flag) and the member has unread People Browser moments, Bizy
// shows a friendly time-of-day callout summarizing who's worth recognizing.
// The callout is dismissed for the segment once the user sends any message.

/** Resolve the time-of-day i18n key ("Good morning/afternoon/evening"). */
export function getTimeOfDayGreetingKey(now: Date = new Date()): string {
	const hour = now.getHours();
	if (hour < 12) return 'greeter.morning';
	if (hour < 17) return 'greeter.afternoon';
	return 'greeter.evening';
}

export interface UseBizyGreeterCalloutOptions {
	/** Feature-gate (People Browser flag). When false the callout never shows. */
	enabled: boolean;
	/** Server signal: the next message would open a fresh segment, not append. */
	isNewSegment: boolean;
	/** Number of unread People Browser entries. Zero → no callout. */
	unreadCount: number;
}

export interface UseBizyGreeterCalloutResult {
	/** Whether to render the greeter callout. */
	showGreeter: boolean;
	/** Time-of-day i18n key for the greeting line. */
	timeOfDayKey: string;
	/** Dismiss the callout for the current segment (call on first message send). */
	dismiss: () => void;
}

export function useBizyGreeterCallout({ enabled, isNewSegment, unreadCount }: UseBizyGreeterCalloutOptions): UseBizyGreeterCalloutResult {
	const [dismissed, setDismissed] = useState(false);

	// Re-arm the callout whenever a fresh segment begins (false → true). Dismissal
	// is in-memory and scoped to the current segment; a later load that detects a
	// new segment boundary shows it again.
	const wasNewSegmentRef = useRef(isNewSegment);
	useEffect(() => {
		if (isNewSegment && !wasNewSegmentRef.current) setDismissed(false);
		wasNewSegmentRef.current = isNewSegment;
	}, [isNewSegment]);

	const dismiss = useCallback(() => setDismissed(true), []);

	const showGreeter = enabled && isNewSegment && unreadCount > 0 && !dismissed;

	return { showGreeter, timeOfDayKey: getTimeOfDayGreetingKey(), dismiss };
}
